import type { SynthPreset } from "../types";

/** Classic electronic pack — techno, house, electro, acid, ambient and DnB-adjacent voices. */
export const ELECTRONIC_PRESETS: readonly SynthPreset[] = [
  // Cold glassy lead: tri body under a shimmering octave saw, resonant peak for the icy top edge.
  {
    name: "Iceberg Lead",
    osc1: { wave: "tri", octave: 0, semis: 0, detuneCents: -9, level: 0.36 },
    osc2: { wave: "saw", octave: 1, semis: 0, detuneCents: 5, level: 0.22 },
    filter: { type: "lowpass", cutoffHz: 3800, resonance: 0.38 },
    ampEnv: { a: 0.012, d: 0.35, s: 0.42, r: 0.55 },
    modEnv: { a: 0.001, d: 0.5, s: 0.12, r: 0.35 },
    lfo: { rateHz: 4.8, wave: "sine", depth: 0.4 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.3 },
      { source: "lfo", target: "cutoff", depth: 0.35 },
      { source: "lfo", target: "pitch", depth: 0.18 },
    ],
    glideMs: 26,
  },
  // 303-style acid bass: screaming resonance, fast mod-env squelch, glide for slides.
  {
    name: "Acid Serpent",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: 0, level: 0.6 },
    osc2: { wave: "square", octave: -1, semis: 0, detuneCents: 3, level: 0.18, pwm: 0.35 },
    filter: { type: "lowpass", cutoffHz: 220, resonance: 0.82 },
    ampEnv: { a: 0.003, d: 0.35, s: 0.55, r: 0.12 },
    modEnv: { a: 0.001, d: 0.22, s: 0, r: 0.08 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 3.2 }],
    glideMs: 45,
  },
  // DnB reese: two hard-detuned saws grinding against each other under slow filter drift.
  {
    name: "Reese Undertow",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: -14, level: 0.42 },
    osc2: { wave: "saw", octave: -1, semis: 0, detuneCents: 16, level: 0.4 },
    filter: { type: "lowpass", cutoffHz: 380, resonance: 0.3 },
    ampEnv: { a: 0.02, d: 0.5, s: 0.8, r: 0.3 },
    modEnv: { a: 0.005, d: 0.4, s: 0.25, r: 0.2 },
    lfo: { rateHz: 0.35, wave: "tri", depth: 0.6 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.8 },
      { source: "lfo", target: "cutoff", depth: 0.9 },
    ],
    glideMs: 30,
  },
  // Techno chord stab: saw plus a fifth, hard resonant snap, no sustain — hits and gone.
  {
    name: "Warehouse Stab",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: 0, level: 0.44 },
    osc2: { wave: "saw", octave: 0, semis: 7, detuneCents: -6, level: 0.3 },
    filter: { type: "lowpass", cutoffHz: 1400, resonance: 0.55 },
    ampEnv: { a: 0.002, d: 0.16, s: 0, r: 0.12 },
    modEnv: { a: 0.001, d: 0.09, s: 0, r: 0.04 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 2.6 }],
    glideMs: 8,
  },
  // Rave hoover: detuned saw over a pulse-width-swept square, with the signature falling pitch scoop.
  {
    name: "Hoover Riot",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -18, level: 0.4 },
    osc2: { wave: "square", octave: -1, semis: 0, detuneCents: 14, level: 0.32, pwm: 0.3 },
    filter: { type: "lowpass", cutoffHz: 1800, resonance: 0.42 },
    ampEnv: { a: 0.01, d: 0.3, s: 0.7, r: 0.25 },
    modEnv: { a: 0.001, d: 0.18, s: 0, r: 0.05 },
    lfo: { rateHz: 5.2, wave: "tri", depth: 0.5 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 7 },
      { source: "modEnv", target: "cutoff", depth: 2.2 },
      { source: "lfo", target: "pwm", depth: 3 },
    ],
    glideMs: 35,
  },
  // Electro pluck: narrow pulse through a resonant bandpass, wet and pointed, good for 16th arps.
  {
    name: "Neon Rain Pluck",
    osc1: { wave: "square", octave: 0, semis: 0, detuneCents: 0, level: 0.4, pwm: 0.22 },
    osc2: { wave: "tri", octave: 1, semis: 0, detuneCents: 7, level: 0.2 },
    filter: { type: "bandpass", cutoffHz: 2600, resonance: 0.45 },
    ampEnv: { a: 0.001, d: 0.14, s: 0.05, r: 0.12 },
    modEnv: { a: 0.001, d: 0.06, s: 0, r: 0.03 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 1.8 }],
  },
  // Chicago house organ: sine fundamental plus octave pulse, instant on/off with a gentle tremolo.
  {
    name: "Basement Organ",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0.38 },
    osc2: { wave: "square", octave: 1, semis: 0, detuneCents: -5, level: 0.26, pwm: 0.5 },
    filter: { type: "lowpass", cutoffHz: 2600, resonance: 0.15 },
    ampEnv: { a: 0.004, d: 0.05, s: 0.85, r: 0.09 },
    modEnv: { a: 0.001, d: 0.12, s: 0.3, r: 0.06 },
    lfo: { rateHz: 6.2, wave: "sine", depth: 0.35 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.8 },
      { source: "lfo", target: "amp", depth: 0.1 },
    ],
  },
  // Ambient electronica pad: multi-second swell with a very slow filter tide and drifting pitch.
  {
    name: "Aurora Drift",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: -11, level: 0.3 },
    osc2: { wave: "tri", octave: 0, semis: 0, detuneCents: 9, level: 0.26 },
    filter: { type: "lowpass", cutoffHz: 700, resonance: 0.22 },
    ampEnv: { a: 1.2, d: 1.4, s: 0.75, r: 1.8 },
    modEnv: { a: 1.6, d: 2, s: 0.5, r: 1.2 },
    lfo: { rateHz: 0.09, wave: "sine", depth: 0.7 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 2.4 },
      { source: "lfo", target: "cutoff", depth: 0.7 },
      { source: "lfo", target: "pitch", depth: 0.12 },
    ],
    glideMs: 120,
  },
  // Dub techno ping: resonant bandpass blip with a short pitch drop and a long hollow tail.
  {
    name: "Dub Sonar",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0.42 },
    osc2: { wave: "tri", octave: 1, semis: 7, detuneCents: 4, level: 0.15 },
    filter: { type: "bandpass", cutoffHz: 1500, resonance: 0.72 },
    ampEnv: { a: 0.002, d: 0.7, s: 0, r: 0.6 },
    modEnv: { a: 0.001, d: 0.12, s: 0, r: 0.05 },
    lfo: { rateHz: 0.7, wave: "sine", depth: 0.5 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 2 },
      { source: "modEnv", target: "cutoff", depth: 1.5 },
      { source: "lfo", target: "cutoff", depth: 0.3 },
    ],
  },
  // Electro tom: tuned sine with a deep pitch fall and a whisper of noise on the transient.
  {
    name: "Cavern Tom",
    osc1: { wave: "sine", octave: -1, semis: 0, detuneCents: 0, level: 0.72 },
    osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.08 },
    filter: { type: "lowpass", cutoffHz: 900, resonance: 0.25 },
    ampEnv: { a: 0.001, d: 0.42, s: 0, r: 0.12 },
    modEnv: { a: 0.001, d: 0.16, s: 0, r: 0.03 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 14 },
      { source: "modEnv", target: "cutoff", depth: 1.6 },
    ],
  },
];
